import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout.tsx';
import { adminFetch, debugRequest } from '../../utils/adminFetch.ts';

interface DashboardStats {
  userCount: number;
  apiKeyCount: number;
  totalCredits: number;
  dbCollectionsCount: number;
}

interface DatabaseStats {
  user_count: number;
  active_api_key_count: number;
  collection_stats: Record<string, any>;
}

interface CollectionsResponse {
  collections: string[];
}

interface User {
  _id: string;
  email: string;
  credits: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    userCount: 0,
    apiKeyCount: 0,
    totalCredits: 0,
    dbCollectionsCount: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const [dbStatsData, collectionsData, usersData] = await Promise.all([
          adminFetch<DatabaseStats>('/database/stats'),
          adminFetch<CollectionsResponse>('/database/collections'),
          adminFetch<User[]>('/users')
        ]);
        
        const totalCredits = usersData.reduce(
          (sum, user) => sum + (user.credits || 0),
          0
        );
        
        setStats({
          userCount: dbStatsData.user_count || 0,
          apiKeyCount: dbStatsData.active_api_key_count || 0,
          totalCredits,
          dbCollectionsCount: collectionsData.collections?.length || 0
        });
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(`Failed to load dashboard data: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  const runDebugTest = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Try our debug function to test different header combinations
      const result = await debugRequest('/database/stats');
      
      if (result.success) {
        alert(`Success! Found working headers. Check console for details.`);
        // Use the working headers for future requests
        console.log("Working headers:", result.headers);
        
        // Update stats
        setStats({
          userCount: result.data.user_count || 0,
          apiKeyCount: result.data.active_api_key_count || 0,
          totalCredits: 0, // We'll need to fetch users separately
          dbCollectionsCount: Object.keys(result.data.collection_stats || {}).length
        });
      } else {
        alert(`Debug test failed: ${result.message}. Check console for details.`);
      }
    } catch (err: any) {
      console.error("Debug test error:", err);
      setError(`Debug test error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout title="Admin Dashboard">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="ml-4 text-gray-600">Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          {/* Add debug button when errors occur */}
          <button 
            onClick={runDebugTest}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded"
          >
            Run Debug Test
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <DashboardCard 
              title="Total Users" 
              value={stats.userCount} 
              icon="people"
              linkTo="/admin/users"
            />
            <DashboardCard 
              title="Active API Keys" 
              value={stats.apiKeyCount} 
              icon="key"
            />
            <DashboardCard 
              title="Total Credits" 
              value={stats.totalCredits} 
              icon="credit_card"
            />
            <DashboardCard 
              title="Database Collections" 
              value={stats.dbCollectionsCount} 
              icon="database"
              linkTo="/admin/database/export"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div className="bg-base rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold font-space-grotesk mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Link to="/admin/users/create" className="block p-3 bg-upload-button-base rounded-md hover:bg-upload-button-hover flex items-center">
                  <span className="material-icons mr-2">add_circle</span>
                  Create New User
                </Link>
                <Link to="/admin/database/export" className="block p-3 bg-upload-button-base rounded-md hover:bg-upload-button-hover flex items-center">
                  <span className="material-icons mr-2">download</span>
                  Export Database
                </Link>
              </div>
            </div>

            <div className="bg-base rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold font-space-grotesk mb-4">System Health</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-base rounded-md">
                  <span className="font-medium">API Status</span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Online</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-base rounded-md">
                  <span className="font-medium">Database Connection</span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Connected</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

interface DashboardCardProps {
  title: string;
  value: number;
  icon: string;
  linkTo?: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, value, icon, linkTo }) => {
  const content = (
    <div className="bg-base rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-dark">{title}</p>
          <p className="text-3xl font-bold mt-1 font-space-grotesk">{value.toLocaleString()}</p>
        </div>
        <div className="bg-upload-button-hover rounded-full p-3 text-black">
          <span className="material-icons">{icon}</span>
        </div>
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }

  return content;
};

export default AdminDashboard;
